<template>
  <div class="w-full h-screen relative" :class="tableauState.isLoading ? 'mt-8 overflow-hidden' : 'pt-0'">
    <Spinner v-if="tableauState.isLoading" class="h-screen" />
    <uhHoPage v-if="tableauState.timedOut && !tableauState.isLoading" />
    <div
      v-if="!tableauState.timedOut"
      ref="vizContainer"
      :class="tableauState.isLoading ? 'invisible' : 'visible'"
    ></div>
    <slot name="body"></slot>
  </div>
</template>

<script setup>
/**
 * TableauVizLoader Component
 * This component is responsible for loading a Tableau visualization into a specified container,
 * handling loading states, timeouts, and errors.
 */

import { datadogLogs } from "@datadog/browser-logs";
import { TableauEventType, TableauViz } from "@tableau/embedding-api";
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import uhHoPage from "@/components/uhHoPage.vue";
import { TABLEAU_VIZ_FRAME_HEIGHT, TABLEAU_VIZ_FRAME_WIDTH } from "@/constants.js";
import Spinner from "../helpers/Spinner.vue";

defineOptions({
  name: "TableauVizLoader",
});

const props = defineProps({
  /**
   * The URL source for the Tableau visualization.
   * @type {string}
   * @required
   */
  src: {
    type: String,
    required: true,
  },
  /**
   * Toolbar visibility for the Tableau visualization.
   * @type {string}
   * @default "hidden"
   */
  toolbar: {
    type: String,
    required: false,
    default: "hidden",
  },
  /**
   * Width of the Tableau visualization frame.
   * @type {string}
   * @default TABLEAU_VIZ_FRAME_WIDTH
   */
  width: {
    type: String,
    required: false,
    default: TABLEAU_VIZ_FRAME_WIDTH,
  },
  /**
   * Height of the Tableau visualization frame.
   * @type {string}
   * @default TABLEAU_VIZ_FRAME_HEIGHT
   */
  height: {
    type: String,
    default: TABLEAU_VIZ_FRAME_HEIGHT,
  },
  /**
   * Array of filters to apply to the Tableau visualization.
   * Each filter object must include a `name` and `value`.
   * @type {Array<Object>}
   * @default []
   */
  filters: {
    type: Array,
    required: false,
    default: () => [],
  },
  /**
   * Function to let VizLoader now if should add that to the event listener using this function there.
   * @type {Function}
   * @default () => {}
   */
  onMarksSelection: {
    type: Function,
    required: false,
    default: () => {},
  },
  /**
   * Tableau report token used for authorization on the tableau server side.
   * @type {string}
   * @default undefined
   */
  token: {
    type: String,
    required: false,
  },
});

const vizContainer = ref(null); // Reference to the container for the Tableau visualization

const tableauState = reactive({
  vizSizeDetected: false, // Tracks if the Tableau visualization size is detected
  vizCompletedWithError: false, // Tracks if the Tableau visualization completed with an error
  vizCompleted: false, // Tracks if the Tableau visualization completed
  vizInteractive: false, // Tracks if the Tableau visualization is interactive
  isLoading: true, // Tracks the loading state
  timedOut: false, // Tracks if the visualization loading timed out
});
const viz = ref(undefined); // TableauViz instance

const stopLoading = () => {
  if (tableauState.isLoading) {
    tableauState.isLoading = false;
  }
};

const updateVizState = (stateObj, key, newState = true, logMessage = "") => {
  if (!stateObj[key]) {
    stateObj[key] = newState;
    if (logMessage) {
      console.log(logMessage);
    }
  }
};

const handleVizSizeDetected = () => {
  if (!tableauState.vizSizeDetected) {
    tableauState.vizSizeDetected = true;
  }
};

const applyFilters = () => {
  // eslint-disable-next-line no-undef
  if (!_.isEmpty(props.filters)) {
    // eslint-disable-next-line no-undef
    _.forEach(props.filters, (obj) => {
      try {
        if (obj && obj.name && obj.value) {
          viz.value.addFilter(obj.name, obj.value);
        }
      } catch (error) {
        datadogLogs.logger.warn("Failed to apply filter", {
          location: "VizLoader",
          order: error,
        });
      }
    });
  }
};

const messageEventListener = (e) => {
  // Validate the origin
  if (e.origin !== "https://tableau.apiabroad.com") return;

  // Ensure e.data is a string and matches the expected format
  if (typeof e.data !== "string" || !e.data.startsWith("tableau.completed")) {
    return;
  }

  const status = e.data.split(",")?.[1]; // Safely extract `status`, which may be undefined or falsy

  if (status && status === "-1") {
    updateVizState(tableauState, "vizCompletedWithError", true);
  } else {
    updateVizState(tableauState, "vizCompleted", true);
  }
};

const showOktaWidget = computed(
  () => tableauState.vizCompleted && !tableauState.vizCompletedWithError && !tableauState.vizSizeDetected,
);

const showViz = computed(() => {
  return showOktaWidget.value || (!tableauState.isLoading && !tableauState.timedOut);
});

onMounted(() => {
  if (vizContainer.value) {
    viz.value = new TableauViz();
    viz.value.src = props.src;
    viz.value.toolbar = props.toolbar;
    viz.value.width = props.width;
    viz.value.height = props.height;

    if (props.token) {
      viz.value.token = props.token;
    }

    applyFilters();
    viz.value.addEventListener(TableauEventType.FirstInteractive, () => {
      updateVizState(tableauState, "vizInteractive", true, "Visualization is now interactive.");
      stopLoading();
    });
    viz.value.addEventListener(TableauEventType.FirstVizSizeKnown, () => {
      updateVizState(tableauState, "vizSizeDetected", true, "Visualization size detected.");
    });
    if (props.onMarksSelection) {
      viz.value.addEventListener(TableauEventType.MarkSelectionChanged, props.onMarksSelection);
    }

    // Listen for iframe postMessage, tableau may complete but not emit a size or interactive events, eg okta widger
    window.addEventListener("message", messageEventListener);
    vizContainer.value.appendChild(viz.value);
    setTimeout(() => {
      if (tableauState.isLoading && !showViz.value) {
        tableauState.timedOut = true;
        stopLoading();
      }
    }, 20000);
  }
});

watch(
  () => [showOktaWidget.value],
  () => {
    if (showOktaWidget.value) {
      stopLoading();
    }
  },
);

onUnmounted(() => {
  try {
    // Clean up viz event listeners
    if (viz.value) {
      viz.value.removeEventListener(TableauEventType.FirstVizSizeKnown, handleVizSizeDetected);
      viz.value.removeEventListener(TableauEventType.FirstInteractive, stopLoading);
      if (props.onMarksSelection) {
        viz.value.removeEventListener(TableauEventType.MarkSelectionChanged, props.onMarksSelection);
      }
    }
  } catch (error) {
    datadogLogs.logger.warn("Error during viz event listener cleanup", {
      location: "VizLoader",
      order: error,
    });
  } finally {
    // Ensure window event listener is always removed
    window.removeEventListener("message", messageEventListener);
  }
});
</script>
